// lib/docs-content.ts
// 产品使用文档内容（结构化数据，供 /docs 页面渲染和搜索）
// 每个文档块包含：id、分类、标题、关键词、Markdown 正文

export interface DocSection {
  id: string;
  category: string;
  title: string;
  keywords: string[];
  content: string;
}

export const DOC_CATEGORIES = [
  "快速开始",
  "核心功能",
  "AI 能力",
  "数据与同步",
  "常见问题",
] as const;

export const DOC_SECTIONS: DocSection[] = [
  // ===================== 快速开始 =====================
  {
    id: "getting-started",
    category: "快速开始",
    title: "30 秒快速上手",
    keywords: ["开始", "入门", "新手", "引导", "onboarding"],
    content: `devpath 是一个 AI 驱动的个人学习路径管理工具，帮你把"想学什么"变成可执行的计划。

## 三步开始

1. **输入主题**：在首页或「学习」页输入你想学的方向（如"React 性能优化"、"系统设计"）
2. **AI 生成计划**：AI 自动拆解知识树、排日程、出面试题
3. **开始学习**：按计划逐日学习，完成后自动打卡

## 首页能看到什么

- **今日待学 / 待复习**：当天需要学习和复习的数量
- **连续打卡**：完成当天任务自动打卡，连续天数越多火焰越旺
- **AI 每日建议**：AI 根据你的进度主动给出学习建议

> 提示：无需注册账号，所有数据默认存在浏览器本地（IndexedDB），可随时开启云端同步。`,
  },
  {
    id: "preset-knowledge",
    category: "快速开始",
    title: "预设知识库",
    keywords: ["预设", "模板", "知识库", "preset", "内置"],
    content: `不想从零开始？内置了多个方向的知识库模板，一键导入即可使用。

## 使用方法

1. 进入「学习」页
2. 找到「内置知识库」区域
3. 选择一个方向（如前端、后端、算法等）
4. 点击即可导入，无需 AI 生成

## 预设模板不需要 AI

预设知识库是预先整理好的知识结构和面试题，不需要配置 AI 模型也能使用。适合先体验产品功能，再决定是否配置自己的 AI。

> 如果你想自定义学习方向，在输入框中输入主题后点击「生成计划」，AI 会帮你创建专属知识树。`,
  },

  // ===================== 核心功能 =====================
  {
    id: "learning-plan",
    category: "核心功能",
    title: "学习计划与知识树",
    keywords: ["学习计划", "知识树", "知识图谱", "思维导图", "plan", "schedule"],
    content: `学习计划是 devpath 的核心：AI 根据你的主题自动拆解知识点，生成可执行的多日学习日程。

## 知识树（思维导图）

每个学习计划都包含一棵知识树：
- **节点**：每个知识点，标注难度（1-5 星）和出现频率
- **大厂标记**：标注了大厂高频考点的知识点会高亮显示
- **依赖关系**：知识点之间的前置依赖

点击知识树中任意节点可直接开始学习该知识点。

## 日程安排

AI 会把知识点分配到每一天：
- 每天学习 2-3 个知识点
- 可调整、延后、跳过某天的任务
- 支持冻结计划（暂停推进）

## 调整计划

在计划详情页可以：
- **重新生成**：让 AI 重新拆解知识树
- **手动编辑**：修改知识点标题、难度、依赖关系
- **调整日程**：延后、跳过、重新分配某天的任务`,
  },
  {
    id: "review-system",
    category: "核心功能",
    title: "间隔复习（FSRS）",
    keywords: ["复习", "FSRS", "间隔重复", "遗忘曲线", "review", "闪卡"],
    content: `devpath 使用 FSRS（Free Spaced Repetition Scheduler）算法安排复习，科学对抗遗忘。

## 复习卡片从哪来

- 学习知识点时生成的面试题会自动加入复习队列
- 收藏的题目也会进入复习
- 错题本中的错题会重点复习

## 复习评分

每张卡片复习后选择评分：
1. **Again（重来）**：完全忘了，重新安排
2. **Hard（困难）**：想起来了但很费劲
3. **Good（良好）**：正常回忆起来
4. **Easy（简单）**：非常轻松

FSRS 会根据你的评分动态调整每张卡片的下次复习时间，记得越牢复习间隔越长。

## 复习提醒

首页「今日待复习」显示当天到期卡片数，点击进入复习。建议每天固定时间复习以保持节奏。`,
  },
  {
    id: "mistake-book",
    category: "核心功能",
    title: "错题本",
    keywords: ["错题", "错题本", "mistake", "复习错题"],
    content: `错题本自动收集你做错或标记的题目，帮你针对性查漏补缺。

## 错题从哪来

- 面试题答错后自动加入
- 复习时评 "Again" 的题目
- 手动标记的题目

## 功能

- **错误次数**：显示每道题被答错的次数
- **掌握标记**：复习正确后可标记"已掌握"
- **重新练习**：点击错题可重新查看答案和练习

> 建议：每周回顾一次错题本，把反复出错的题目重点复习。`,
  },
  {
    id: "emotion-tracking",
    category: "核心功能",
    title: "情绪与状态追踪",
    keywords: ["情绪", "状态", "心情", "emotion", "精力", "打卡"],
    content: `学习不只是知识，状态同样重要。devpath 帮你追踪每日精力和情绪变化。

## 每日状态评估

在首页可以快速选择今日状态：
- **不太好**（精力 2/5）
- **一般**（精力 3/5）
- **状态不错**（精力 4/5）

AI 会根据你的状态自动调整当日学习负载。

## 情绪日志

在「情绪」页记录详细情绪：
- 选择情绪标签（焦虑、兴奋、疲惫等）
- 记录触发原因和影响
- 写下应对方式

## 情绪分析

情绪页提供情绪分布统计，帮你发现情绪模式和规律。`,
  },
  {
    id: "dashboard",
    category: "核心功能",
    title: "仪表盘与数据统计",
    keywords: ["仪表盘", "统计", "dashboard", "数据", "雷达图", "热力图"],
    content: `仪表盘汇总你的学习数据，一目了然。

## 可视化内容

- **雷达图**：各知识领域掌握度分布
- **热力图**：每日学习活跃度（类似 GitHub 贡献图）
- **学习时长**：累计和每日学习时间
- **连续打卡**：当前和历史最长连续天数

## 公开主页

你可以开启公开主页，分享你的学习进度：
1. 在「我的 → 个人信息」中设置用户名和展示内容
2. 选择公开哪些数据（雷达图、热力图、当前主题等）
3. 保存后访问 \`/u/你的用户名\` 即可查看

> 公开主页数据存储在 Cloudflare KV，跨设备同步。`,
  },

  // ===================== AI 能力 =====================
  {
    id: "ai-model-config",
    category: "AI 能力",
    title: "AI 模型配置",
    keywords: ["模型", "配置", "API Key", "OpenAI", "DeepSeek", "GLM", "Kimi", "预设"],
    content: `devpath 支持接入你自己的 AI 模型（OpenAI 兼容接口），所有 AI 功能都由你配置的模型驱动。

## 如何配置

1. 进入「我的 → AI 模型配置」
2. 点击「添加模型」
3. 选择预设模板或手动填写：
   - **名称**：自定义标记
   - **提供商**：glm / deepseek / mimo / kimi / custom
   - **baseURL**：API 地址
   - **API Key**：从模型服务商获取的密钥
   - **模型名**：如 glm-4-flash、deepseek-v4-flash
4. 保存后可点击「测试」验证连接

## 预设模板

内置以下模板，点击即可自动填充：
- **智谱 GLM**（glm-4-flash，免费额度）
- **DeepSeek**（deepseek-v4-flash）
- **小米 MiMo**（mimo-v2-pro）
- **Kimi**（moonshot-v1-8k，Moonshot AI）
- **OpenAI**（gpt-4o-mini）
- **通义千问**（qwen-plus）

## 设为默认

可以将某个模型设为默认，新对话和题目生成会优先使用它。

> API Key 存储在浏览器本地 IndexedDB，不会上传到云端。换设备后需要在「我的 → 云端同步」恢复或重新配置。`,
  },
  {
    id: "ai-chat",
    category: "AI 能力",
    title: "AI 聊天助手",
    keywords: ["聊天", "对话", "AI", "chat", "多轮对话", "工具"],
    content: `AI 聊天助手可以回答学习问题、查看你的日程、创建提醒等。

## 基本用法

- 在「AI」页输入问题发送
- 支持多轮对话，AI 会结合上下文回答
- 可创建多个对话，支持搜索历史

## 快捷指令

AI 可以执行以下操作：
- "今天有什么安排？" — 查看今日日程
- "接下来该学什么？" — 查看学习进度
- "30分钟后提醒我学习" — 创建定时提醒
- "复盘一下今天的表现" — 生成今日总结

## 消息操作

- **重新生成**：对 AI 回复不满意可重新生成
- **删除消息**：多轮对话中删除某条消息
- **反馈**：点 👎 标记回复质量，帮助优化

## 提示词库

内置常用提示词，点击即可快速填入输入框。也支持保存自定义提示词。`,
  },
  {
    id: "question-generation",
    category: "AI 能力",
    title: "面试题生成",
    keywords: ["面试题", "题目", "生成", "question", "大厂", "追问"],
    content: `AI 根据知识点自动生成面试题，帮你检验学习成果。

## 题目包含

- **问题**：面试常见问题
- **参考答案**：结构化答案（支持 Markdown 格式）
- **关键点**：答题要点列表
- **追问**：面试官可能的深入追问
- **代码示例**：相关代码片段
- **大厂标记**：是否大厂高频题

## 操作

- **展开答案**：点击题目查看答案
- **换一题**：重新生成该知识点的题目
- **收藏**：收藏好题，进入复习队列
- **追问提问**：点击追问直接进入 AI 聊天深入讨论

## 失败重试

偶尔 AI 生成可能失败（网络波动或字段缺失），点击「重新生成」即可重试。系统已对常见字段缺失做了容错处理。`,
  },

  // ===================== 数据与同步 =====================
  {
    id: "cloud-sync",
    category: "数据与同步",
    title: "云端同步与跨设备",
    keywords: ["同步", "云端", "跨设备", "sync", "恢复", "KV", "Cloudflare"],
    content: `devpath 支持云端同步，让你在多设备间无缝切换。

## 数据存储

- **本地优先**：所有数据先存入浏览器 IndexedDB
- **云端备份**：开启同步后自动备份到 Cloudflare KV
- **自动同步**：数据变更后自动触发同步（防抖）

## 跨设备迁移

1. **旧设备**：在「我的 → 云端同步」点击「上传到云端」
2. 复制你的用户 ID
3. **新设备**：点击「导入已有 ID」，粘贴用户 ID
4. 点击「从云端恢复」

## 同步内容

同步的数据包括：
- 学习计划和进度
- 收藏的题目和卡片
- AI 模型配置
- 个人信息和情绪日志
- 错题本

> API Key 不会同步到云端（安全考虑），换设备后需重新配置 AI 模型。`,
  },
  {
    id: "api-token",
    category: "数据与同步",
    title: "API 鉴权 Token",
    keywords: ["token", "鉴权", "API Token", "授权", "安全"],
    content: `API Token 是部署层面的鉴权密钥，与 AI 模型的 API Key 不同。

## 什么时候需要

- **大多数用户**：不需要填写。只要配置了自己的 AI 模型（含 API Key），即可使用所有功能
- **使用服务端默认模型**：如果部署方配置了服务端默认模型并设置了 \`API_TOKEN\` 环境变量，你需要在此填入相同的 Token

## API Key vs API Token

| | API Key | API Token |
|---|---|---|
| 用途 | 调用你自己的 AI 模型 | 访问部署方的服务端模型 |
| 获取 | 模型服务商（智谱/DeepSeek等） | 部署方提供 |
| 存储 | 浏览器本地 | 浏览器本地 |
| 必需 | 是（使用 AI 功能） | 否（仅服务端模型场景） |

> 如果你已在「AI 模型配置」中添加了自己的模型，直接忽略 API Token 即可。`,
  },
  {
    id: "public-profile",
    category: "数据与同步",
    title: "公开主页与分享",
    keywords: ["公开主页", "分享", "profile", "用户页", "/u/"],
    content: `开启公开主页后，其他人可以通过链接查看你的学习进度。

## 设置方法

1. 进入「我的 → 个人信息」
2. 填写用户名（用于访问 URL）和展示名
3. 选择公开内容：
   - 雷达图
   - 热力图
   - 当前学习主题
   - 学习笔记
4. 点击保存

## 访问地址

保存后，访问 \`/u/你的用户名\` 即可查看公开主页。

## 同步问题排查

如果访问公开主页显示"用户不存在"：
1. 确认在「我的」页保存过个人信息
2. 检查是否出现同步错误提示（可能是未授权）
3. 如提示 401 未授权：在「设置 → API 鉴权 Token」填入与部署方一致的 Token

> 公开主页数据存储在 Cloudflare KV，保存时会自动同步到云端。`,
  },

  // ===================== 常见问题 =====================
  {
    id: "faq-ai-error",
    category: "常见问题",
    title: "AI 接口失败怎么办",
    keywords: ["失败", "报错", "AI", "错误", "503", "连接"],
    content: `AI 功能无法使用时，按以下步骤排查：

## 常见原因

1. **未配置 AI 模型**（最常见）
   - 前往「我的 → AI 模型配置」添加模型
   - 必须填写 API Key

2. **模型未选中**
   - 在聊天页底部确认模型选择器已选中你配置的模型

3. **API Key 无效或余额不足**
   - 在模型配置页点击「测试」验证连接
   - 检查 API Key 是否正确、是否有余额

4. **网络问题**
   - 检查网络连接
   - 部分 API 需要代理访问

## 仍然失败

- 预设知识库不需要 AI，可先使用预设体验功能
- 检查 baseURL 是否正确（需以 \`/v1\` 结尾）
- 确认模型名称拼写正确`,
  },
  {
    id: "faq-data-loss",
    category: "常见问题",
    title: "数据丢失或清空",
    keywords: ["数据丢失", "清空", "恢复", "清除缓存", "换浏览器"],
    content: `devpath 数据默认存在浏览器本地，清除浏览器数据会导致丢失。

## 预防

- 开启「云端同步」，数据自动备份到 Cloudflare KV
- 记住你的用户 ID（在「我的 → 云端同步」查看）

## 恢复方法

1. 进入「我的 → 云端同步」
2. 点击「导入已有 ID」
3. 输入之前的用户 ID
4. 点击「从云端恢复」

## 注意

- 不同浏览器数据不互通（除非开启同步）
- 隐私模式下数据关闭后即丢失
- 定期检查同步状态确保数据已上传`,
  },
  {
    id: "faq-shortcuts",
    category: "常见问题",
    title: "快捷操作与小技巧",
    keywords: ["快捷键", "技巧", "tips", "效率"],
    content: `一些提升效率的小技巧：

## 学习效率

- **追问深入**：面试题的"追问"可直接点击进入 AI 聊天
- **快速收藏**：题目卡片右上角星标一键收藏
- **知识树跳转**：点击知识树节点直接学习该知识点

## 复习策略

- 每天固定时间复习（建议睡前 1 小时）
- 对模糊的题目选 Hard 而非 Again，避免复习堆积
- 每周清理一次错题本

## 数据管理

- 长时间不用时可冻结计划，进度不丢失
- 公开主页可选择性展示数据
- 情绪日志帮你发现学习状态规律

## AI 使用

- 多轮对话比单次提问效果更好
- 用具体问题代替模糊提问（"解释 React useEffect 的清理函数"优于"讲讲 useEffect"）
- 善用快捷指令快速获取信息`,
  },
  {
    id: "faq-supported-languages",
    category: "常见问题",
    title: "支持的技术与语言",
    keywords: ["语言", "技术栈", "代码高亮", "支持"],
    content: `devpath 界面为中文，代码示例支持多种语言高亮。

## 代码高亮支持

- JavaScript / TypeScript / JSX / TSX
- Python
- Java
- Go
- SQL
- Bash / Shell
- CSS

## 技术栈

- **前端**：Next.js 15 + React 19 + TypeScript + Tailwind CSS
- **运行时**：Cloudflare Pages Functions（Edge Runtime）
- **存储**：IndexedDB（本地） + Cloudflare KV（云端）
- **AI**：Vercel AI SDK + OpenAI 兼容接口
- **复习算法**：FSRS（Free Spaced Repetition Scheduler）

## 兼容性

- 支持 Chrome、Edge、Firefox、Safari 等现代浏览器
- 支持 PWA 安装到桌面/手机主屏
- 支持深色/浅色/跟随系统主题`,
  },
];
